// Image handling on the main thread. Documents embed images as data URIs;
// this module decodes them into small luminance grids for the generation
// worker (cached per URI) and downscales incoming files before embedding.
// Images never leave the browser.

import type { AssetMap, DocumentModel, LumaGrid } from '../core/types'
import { CHANNEL_IDS } from '../core/types'

const LUMA_SIZE = 256 // long side of the luminance grid
const IMPORT_MAX_PX = 1024 // long side of the embedded image
export const EMBED_WARN_BYTES = 1_500_000

const lumaCache = new Map<string, Promise<LumaGrid>>()

function loadImage(dataUri: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('image decode failed'))
    img.src = dataUri
  })
}

function decodeLuma(dataUri: string): Promise<LumaGrid> {
  let p = lumaCache.get(dataUri)
  if (!p) {
    p = loadImage(dataUri).then((img) => {
      const scale = Math.min(1, LUMA_SIZE / Math.max(img.width, img.height))
      const w = Math.max(1, Math.round(img.width * scale))
      const h = Math.max(1, Math.round(img.height * scale))
      const canvas = document.createElement('canvas')
      canvas.width = w
      canvas.height = h
      const ctx = canvas.getContext('2d', { willReadFrequently: true })
      if (!ctx) throw new Error('2d context unavailable')
      ctx.drawImage(img, 0, 0, w, h)
      const px = ctx.getImageData(0, 0, w, h).data
      const data = new Float32Array(w * h)
      for (let i = 0; i < w * h; i++) {
        // Rec. 709 luma.
        data[i] = (0.2126 * px[i * 4] + 0.7152 * px[i * 4 + 1] + 0.0722 * px[i * 4 + 2]) / 255
      }
      return { w, h, data }
    })
    lumaCache.set(dataUri, p)
  }
  return p
}

// Decode every image the document's channels reference. Failures are skipped:
// the field then simply ignores that image controller.
export async function collectAssets(doc: DocumentModel): Promise<AssetMap> {
  const uris = new Set<string>()
  for (const id of CHANNEL_IDS) {
    const img = doc.channels[id].image
    if (img && img.enabled && img.dataUri) uris.add(img.dataUri)
  }
  const out: AssetMap = {}
  await Promise.all(
    [...uris].map(async (u) => {
      try {
        out[u] = await decodeLuma(u)
      } catch {
        // Undecodable image: leave it out.
      }
    }),
  )
  return out
}

export interface ImportedImage {
  dataUri: string
  width: number // px
  height: number
}

// Read a picked file and embed it, downscaled to a sane size. JPEG re-encode
// keeps photos compact; small files keep their original encoding.
export async function importImageFile(file: File): Promise<ImportedImage> {
  const original: string = await new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(String(r.result))
    r.onerror = () => reject(new Error('file read failed'))
    r.readAsDataURL(file)
  })
  const img = await loadImage(original)
  const scale = Math.min(1, IMPORT_MAX_PX / Math.max(img.width, img.height))
  if (scale === 1 && original.length < 400_000) {
    return { dataUri: original, width: img.width, height: img.height }
  }
  const w = Math.max(1, Math.round(img.width * scale))
  const h = Math.max(1, Math.round(img.height * scale))
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('2d context unavailable')
  ctx.drawImage(img, 0, 0, w, h)
  return { dataUri: canvas.toDataURL('image/jpeg', 0.85), width: w, height: h }
}

export function pickImageFile(onLoaded: (img: ImportedImage) => void, onFail: () => void) {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = 'image/png,image/jpeg,image/webp'
  input.onchange = async () => {
    const file = input.files?.[0]
    if (!file) return
    try {
      onLoaded(await importImageFile(file))
    } catch {
      onFail()
    }
  }
  input.click()
}
